import React, { ChangeEvent, Dispatch, HTMLProps, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, SortingState, VisibilityState, useReactTable, Row } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ReactComponent as ToolsIcon } from '../../assets/icons/tools.svg'
import { ReactComponent as TriangleIcon } from '../../assets/icons/triangle.svg'
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg'
import { ReactComponent as SaveIcon } from '../../assets/icons/save.svg'
import SimpleBar from 'simplebar-react'
import 'simplebar/dist/simplebar.min.css'
import s from './index.module.scss'
import classNames from 'classnames'
import Input from '../UI/Input'
import Checkbox from '../UI/Checkbox'
import { ISelectOption } from '../../models/models'
import Select from '../UI/Select'

interface IProps {
	data: any[]
	columns: ColumnDef<any>[]
	editableColumns?: { 
		name: string; 
		props?: Omit<HTMLProps<HTMLInputElement>, 'type'> & { 
			type: 'select' | HTMLProps<HTMLInputElement>['type'] 
			options?: ISelectOption[]; 
		}
	}[]
	visibleColumns?: VisibilityState
	minHeight?: string
	maxHeight?: string
	sortHandler?: (id: string, direction: 'asc' | 'desc') => void
	showSettings?: () => void
	showDetail?: (id: string | number) => void
	onDynamicFetch?: () => void
	editHandler?: (id: string | number, value: any) => void
	enableRowSelection?: boolean
	setSelectedRows?: Dispatch<SetStateAction<Row<any>[]>>
	selectFieldName?: string
}

function IndeterminateCheckbox({ indeterminate, ...rest }: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
	const ref = useRef<HTMLInputElement>(null!)

	useEffect(() => {
		if (typeof indeterminate === 'boolean') {
			ref.current.indeterminate = !rest.checked && indeterminate
		}
	}, [ref, indeterminate])

	return <Checkbox {...rest} ref={ref} />
}

const Table: React.FC<IProps> = React.memo(
	({
		data,
		columns,
		editableColumns,
		visibleColumns,
		minHeight,
		maxHeight = '100%',
		sortHandler,
		showSettings,
		showDetail,
		onDynamicFetch,
		editHandler,
		enableRowSelection,
		setSelectedRows,
		selectFieldName,
	}) => {
		const [tableData, setTableData] = useState(data)
		const [editableId, setEditableId] = useState<string | number>('')
		const [rowSelection, setRowSelection] = useState({})

		const currentColumns = useMemo((): ColumnDef<any>[] => {
			let newTableData: any[] = [...tableData]

			const cols: ColumnDef<any>[] = columns.map((i: ColumnDef<any>) => ({
				...i,
				cell: (value) => {
					const key = value.cell.id.split('_')[1]
					const editableCol = editableColumns?.find((i) => i.name === key)

					if (value.row.original.id === editableId && editableCol) {
						const onChange = (ev: ChangeEvent<any>) => {
							setTableData((prev) => {
								const newData = prev.map((row, index) =>
									index === value.row.index
										? {
												...prev[value.row.index],
												[value.column.id]: editableCol?.props?.type === 'checkbox' ? ev.target?.checked : ev.target.value,
											}
										: row,
								)
								newTableData = newData
								return newData
							})
						}

						switch (editableCol?.props?.type) {
							case 'checkbox':
								return (
									<Checkbox 
										checked={value.getValue<any>()} 
										onChange={onChange}
									/>
								)
							case 'select':
								return (
									<Select
										value={value.getValue<any>()} 
										onChange={onChange}
										options={editableCol?.props?.options || []}
									/>
								)
							default:
								return (
									<Input 
										{...editableCol?.props} 
										className={s.editInput} 
										value={value.getValue<any>()} 
										onChange={onChange}
									/>
								)
						}
					} else {
						return i?.cell && typeof i.cell === 'function' ? i.cell(value) : value.getValue<any>()
					}
				},
			}))

			if (enableRowSelection) {
				cols.unshift({
					id: 'select',
					header: ({ table }) => (
						<IndeterminateCheckbox
							{...{
								checked: table.getIsAllRowsSelected(),
								indeterminate: table.getIsSomeRowsSelected(),
								onChange: table.getToggleAllRowsSelectedHandler(),
							}}
						/>
					),
					cell: ({ row }) => (
						<div onClick={(ev) => ev.stopPropagation()}>
							<IndeterminateCheckbox
								{...{
									checked: row.getIsSelected(),
									disabled: !row.getCanSelect(),
									indeterminate: row.getIsSomeSelected(),
									onChange: row.getToggleSelectedHandler(),
								}}
							/>
						</div>
					),
					size: 60,
					minSize: 60,
					enableResizing: false,
				})
			}

			if (showSettings) {
				cols.unshift({
					id: 'settings',
					header: () => <ToolsIcon className={s.settingsIcon} onClick={showSettings} />,
					size: 60,
					minSize: 60,
					enableResizing: false,
				})
			}

			if (editHandler) {
				const onEdit = () => {
					const editedRow = newTableData.filter((i) => i.id === editableId)[0]
					const newValues = Object.fromEntries(Object.entries(editedRow).filter(([key]) => editableColumns?.find((i) => i.name === key)))
					editHandler(editableId, newValues)
					setEditableId('')
				}

				cols.push({
					id: 'edit',
					header: () => null,
					size: 60,
					minSize: 60,
					enableResizing: false,
					cell: (value) =>
						value.row.original.id === editableId ? (
							<span className={s.editIcon} onClick={onEdit}>
								<SaveIcon />
							</span>
						) : (
							<EditIcon className={s.editIcon} onClick={() => setEditableId(value.row.original?.id)} />
						),
				})
			}

			return cols
		}, [columns, editableId])

		const [sorting, setSorting] = useState<SortingState>([])

		const table = useReactTable({
			...(selectFieldName && {
				getRowId: (row: any, index, parent: any) => row[selectFieldName],
			}),
			data: tableData,
			columns: currentColumns,
			defaultColumn: { minSize: 120, enableMultiSort: true },
			columnResizeMode: 'onChange',
			state: { sorting, columnVisibility: visibleColumns, rowSelection },
			enableRowSelection: enableRowSelection,
			onRowSelectionChange: setRowSelection,
			onSortingChange: setSorting,
			getCoreRowModel: getCoreRowModel(),
			getSortedRowModel: sortHandler ? undefined : getSortedRowModel(),
		})

		const scrollableNodeRef = useRef()
		const { rows } = table.getRowModel()

		const rowVirtualizer = useVirtualizer({
			count: rows.length,
			getScrollElement: () => scrollableNodeRef.current,
			estimateSize: () => 70,
			overscan: 10,
		})

		const virtualRows = rowVirtualizer.getVirtualItems()
		const totalSize = rowVirtualizer.getTotalSize()
		const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0
		const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0

		useEffect(() => {
			const [lastItem] = [...virtualRows].reverse()

			if (!lastItem) return

			if (lastItem.index >= rows.length - 1) {
				onDynamicFetch && onDynamicFetch()
			}
		}, [virtualRows])

		useEffect(() => {
			setTableData(data)
		}, [data])

		useEffect(() => {
			if (enableRowSelection && setSelectedRows && selectFieldName) {
				setSelectedRows((prev) => {
					const selectedRows = Array.from(new Map([...prev, ...table.getSelectedRowModel().rows].map((i) => [i.id, i])).values()).filter(
						(i) =>
							!tableData.find((j) => j[selectFieldName] === i.id) || // id с другой страницы
							table.getSelectedRowModel().rows.find((j) => j.id === i.id), // selected id
					)

					return selectedRows
				})
			}
		}, [rowSelection])

		return (
			<div 
				className={s.tableWrapper}
				style={{ minHeight, maxHeight }}
			>
				<SimpleBar style={{ maxHeight }} scrollableNodeProps={{ ref: scrollableNodeRef }}>
					<table className={s.table} style={{ width: table.getTotalSize() }}>
						<thead className={s.thead}>
							{table.getHeaderGroups().map((headerGroup) => (
								<tr key={headerGroup.id} className={s.tr}>
									{headerGroup.headers.map((header) => (
										<th
											key={header.id}
											style={{ width: header.getSize() }}
											className={classNames(s.th, {
												[s.canSort]: header.column.getCanSort(),
											})}
											onClick={
												!sortHandler
													? header.column.getToggleSortingHandler()
													: () => {
															if (header.column.getCanSort() && sortHandler) {
																header.column.toggleSorting(header.column.getIsSorted() === 'asc', false)
																sortHandler(header.column.id, header.column.getIsSorted() === 'asc' ? 'asc' : 'desc')
															}
														}
											}
										>
											{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
											{header.column.getCanSort() && (
												<TriangleIcon
													className={classNames(s.sortIcon, {
														[s.asc]: header.column.getIsSorted() === 'asc',
													})}
												/>
											)}
											{header.column.getCanResize() && (
												<div
													onClick={(ev: React.MouseEvent<HTMLDivElement>) => ev.stopPropagation()}
													onMouseDown={header.getResizeHandler()}
													onTouchStart={header.getResizeHandler()}
													className={classNames(s.resizer, {
														[s.isResizing]: header.column.getIsResizing(),
													})}
												/>
											)}
										</th>
									))}
								</tr>
							))}
						</thead>
						<tbody className={s.tbody}>
							{paddingTop > 0 && (
								<tr>
									<td style={{ height: `${paddingTop}px` }} />
								</tr>
							)}
							{virtualRows.map((virtualRow) => {
								const row = rows[virtualRow.index]
								return (
									<tr key={row.id} className={classNames(s.tr, { [s.trHovered]: showDetail })} onClick={() => showDetail && showDetail(row.original.entityId)}>
										{row.getVisibleCells().map((cell) => (
											<td key={cell.id} className={s.td} style={{ width: cell.column.getSize() }}>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</td>
										))}
									</tr>
								)
							})}
							{paddingBottom > 0 && (
								<tr>
									<td style={{ height: `${paddingBottom}px` }} />
								</tr>
							)}
						</tbody>
					</table>
				</SimpleBar>
			</div>
		)
	},
)

Table.displayName = 'Table'

export default Table
